import React from 'react';
import { bool, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LINE_ITEM_PROVIDER_COMMISSION, propTypes } from '../../util/types';
import { getProviderDisplayValues } from '../../util/displayProvider';
import classNames from 'classnames';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

// Validate the assumption that the commission exists and the amount
// is zero or negative.
const isValidCommission = commissionLineItem => {
  return commissionLineItem.lineTotal instanceof Money && commissionLineItem.lineTotal.amount <= 0;
};

const LineItemProviderCommissionMaybe = props => {
  const { lineItems, transaction, isProvider, intl, isEstimated, feeTooltip } = props;

  const providerCommissionLineItem = lineItems.find(
    item => item.code === LINE_ITEM_PROVIDER_COMMISSION && !item.reversal
  );

  // If commission is passed it will be shown as a fee already reduces from the total price
  let commissionItem = null;

  // Sharetribe Web Template is using the default transaction process (https://www.sharetribe.com/docs/concepts/transaction-process/#sharetribe-flex-default-transaction-process)
  // which containt provider commissions so by default the providerCommissionLineItem should exist.
  // If you are not using provider commisison you might want to remove this whole component from OrderBreakdown.js file.
  if (isProvider) {
    // If manual provider payout metadata exists, prefer to show derived commission
    const protectedData = transaction?.attributes?.protectedData;
    const manual = protectedData?.manualProviderPayoutMetadata;

    if (manual && (manual.originalPayoutTotal || manual.providerPayoutAmount)) {
      // Use helper to derive display values
      const { commissionAmount, currency } = getProviderDisplayValues(transaction);

      if (typeof commissionAmount === 'number') {
        const commissionMoney = new Money(commissionAmount, currency);
        const formattedCommission = formatMoney(intl, commissionMoney, true);

        commissionItem = (
          <div className={css.lineItem}>
            <span className={classNames(css.itemLabel, { [css.itemGray]: !isEstimated })}>
              <FormattedMessage id="BookingBreakdown.commission" />

              {feeTooltip}
            </span>
            <span className={css.itemValue}>{formattedCommission}</span>
          </div>
        );
      }
    } else if (providerCommissionLineItem) {
      if (!isValidCommission(providerCommissionLineItem)) {
        // eslint-disable-next-line no-console
        console.error('invalid commission line item:', providerCommissionLineItem);
        throw new Error('Commission should be present and the value should be zero or negative');
      }

      const commission = providerCommissionLineItem.lineTotal;
      const formattedCommission = commission ? formatMoney(intl, commission, true) : null;

      commissionItem = (
        <div className={css.lineItem}>
          <span className={classNames(css.itemLabel, { [css.itemGray]: !isEstimated })}>
            <FormattedMessage id="BookingBreakdown.commission" />

            {feeTooltip}
          </span>
          <span className={css.itemValue}>{formattedCommission}</span>
        </div>
      );
    }
  }

  return commissionItem;
};

LineItemProviderCommissionMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  transaction: propTypes.transaction.isRequired,
  isProvider: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemProviderCommissionMaybe;
